import React from 'react';

import { Divider, Flex, Stack, Text, TextInput, useMantineTheme } from '@mantine/core';
import { SaveButton } from 'pages/SmartEditor/SmartEditorHeader/SaveButton';
import { SavedIndicator } from 'pages/SmartEditor/SmartEditorHeader/SavedIndicator';
import { SavingIndicator } from 'pages/SmartEditor/SmartEditorHeader/SavingIndicator';
import { useTranslation } from 'react-i18next';
import { Resource } from 'shared/interfaces/Resource';
import { getRelativeReadableTimeString } from 'shared/utils/format';

interface EditorHeaderProps {
  fileName: string;
  resource: Resource;
  isPendingChanges: boolean;
  isLoading: boolean;
  onFileNameChange: (fileName: string) => void;
  onSaveChanges: () => void;
}

export function EditorHeader({
  fileName,
  resource,
  isPendingChanges,
  isLoading,
  onFileNameChange,
  onSaveChanges
}: EditorHeaderProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Save changes on Enter inside file name input
    if (event.key === 'Enter') {
      event.preventDefault();
      onSaveChanges();
    }
  };

  return (
    <Stack w="100%" maw="850px" mx="auto" spacing={8}>
      <Flex align="center" justify="space-between" gap="20px" mt={40} w="100%">
        <Flex gap={8} direction="column" style={{ flex: 1 }}>
          <TextInput
            px={0}
            sx={() => ({
              input: {
                fontSize: '2rem',
                fontWeight: 700,
                lineHeight: 1.3,
                paddingBottom: '4px'
              }
            })}
            variant="unstyled"
            value={fileName}
            onChange={(event) => onFileNameChange(event.target.value)}
            placeholder={t('SMART_EDITOR.UNTITLED')}
            maxLength={30}
            onKeyDown={handleKeyDown}
          />

          <Stack spacing={4}>
            {resource.createdAt && (
              <Text size={13} color={theme.colors.gray[5]}>
                {t('SMART_EDITOR.CREATED')} {getRelativeReadableTimeString(resource.createdAt)}
              </Text>
            )}
          </Stack>
        </Flex>

        <Flex align="center" gap={12}>
          {isLoading ? (
            <SavingIndicator />
          ) : isPendingChanges ? (
            <SaveButton onSaveChanges={onSaveChanges} />
          ) : (
            <SavedIndicator />
          )}
        </Flex>
      </Flex>
      <Divider color={theme.colors.gray[2]} my={6} />
    </Stack>
  );
}
